/**
 * The docked chat panel (design-reference §4.18). A `role="dialog"` region with
 * a header (title + new-conversation + close), a scrolling message stream, and a
 * composer. It manages its own focus (the composer is focused on open), closes on
 * Escape, and — because it only ever renders `useChat`'s messages — can only show
 * data the tenant-scoped API returned for this session.
 *
 * The idle state greets and offers suggestion chips; sending shows a thinking
 * indicator, then the assistant answer (with inline chart + query); a failed
 * request renders an inline error turn with a retry.
 */
import { useEffect, useLayoutEffect, useRef, useState } from 'react';

import type { Brand } from '../../api/types';
import { useSession } from '../../auth/session';
import { useAvatar } from '../../auth/useAvatar';
import { cx } from '../../lib/cx';
import { initialsFor } from '../../lib/initials';
import { UserIcon } from '../../shell/icons';

import { BrandTile } from './BrandTile';
import { CloseGlyph, CollapseGlyph, ExpandGlyph, ResetGlyph, SendGlyph } from './glyphs';
import { BotAnswer } from './BotAnswer';
import { useChat } from './useChat';
import type { AutoSubmit } from './types';

function greetingFor(displayName: string | null): string {
  const firstName = displayName?.trim().split(/\s+/)[0];
  const salutation = firstName != null && firstName !== '' ? `Hi ${firstName}` : 'Hi';
  return (
    `${salutation}! Ask me anything about your document analytics. ` +
    "I answer only from your tenant's data and I'll show you the query behind every number."
  );
}

const SUGGESTIONS: readonly string[] = [
  'How many documents did we scan this week?',
  'Which branch scans the most invoices?',
  "What's our busiest scanner?",
  'Where should we automate first?',
];

export interface ChatPanelProps {
  onClose: () => void;
  /** The signed-in caller's own resolved brand (from `AppShell`'s single `useMyBrand()` call). */
  brand: Brand;
  /** The brand's logo, or null to show the letter mark instead. */
  logoUrl: string | null;
  /** A message to auto-submit when the panel opens (from `docsight:open-chat`). */
  autoSubmit?: AutoSubmit | null;
}

export function ChatPanel({ onClose, brand, logoUrl, autoSubmit = null }: ChatPanelProps): React.JSX.Element {
  const { messages, status, send, retry, reset } = useChat();
  const { principal } = useSession();
  const { url: userAvatarUrl } = useAvatar();
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const lastAutoNonce = useRef<number | null>(null);
  const userInitials = initialsFor(principal?.display_name ?? null);
  const greeting = greetingFor(principal?.display_name ?? null);
  // Not persisted — the panel already remounts fresh on every open, so a
  // reopened panel always starts docked at its default size.
  const [expanded, setExpanded] = useState(false);

  // Focus the composer when the panel opens.
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Escape closes the panel from anywhere while it is open. Capture-phase +
  // stopPropagation so it wins over any global key handler (e.g. review triage).
  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('keydown', onKey, true);
    };
  }, [onClose]);

  // Auto-submit a dispatched message once per nonce.
  useEffect(() => {
    if (autoSubmit === null) return;
    if (lastAutoNonce.current === autoSubmit.nonce) return;
    lastAutoNonce.current = autoSubmit.nonce;
    send(autoSubmit.text);
  }, [autoSubmit, send]);

  // Keep the stream pinned to the newest turn.
  useLayoutEffect(() => {
    const el = listRef.current;
    if (el !== null) el.scrollTop = el.scrollHeight;
  }, [messages, status]);

  const submit = (): void => {
    const el = inputRef.current;
    if (el === null) return;
    const text = el.value;
    if (text.trim() === '') return;
    send(text);
    el.value = '';
    el.focus();
  };

  const isIdle = messages.length === 0 && status !== 'thinking';

  return (
    <section
      className={cx('ds-chat-panel', expanded && 'ds-chat-panel--expanded')}
      role="dialog"
      aria-modal="false"
      aria-label={brand.chatbot_name}
    >
      <header className="ds-chat-header">
        <BrandTile brand={brand} logoUrl={logoUrl} />
        <span className="ds-chat-header__titles">
          <span className="ds-chat-header__title">{brand.chatbot_name}</span>
          <span className="ds-chat-header__sub">Analytics questions about your documents</span>
        </span>
        <button
          type="button"
          className="ds-chat-header__btn"
          aria-label={expanded ? 'Collapse' : 'Expand'}
          title={expanded ? 'Collapse' : 'Expand'}
          onClick={() => {
            setExpanded((current) => !current);
          }}
        >
          {expanded ? <CollapseGlyph /> : <ExpandGlyph />}
        </button>
        <button
          type="button"
          className="ds-chat-header__btn"
          aria-label="New conversation"
          title="New conversation"
          onClick={() => {
            reset();
            inputRef.current?.focus();
          }}
        >
          <ResetGlyph />
        </button>
        <button type="button" className="ds-chat-header__btn" aria-label="Close" title="Close" onClick={onClose}>
          <CloseGlyph size={15} />
        </button>
      </header>

      <div className="ds-chat-list" ref={listRef} data-chat-scroll="1">
        {isIdle && (
          <>
            {/* The opening line is the assistant talking, so it carries the same
                avatar as every other thing it says. */}
            <div className="ds-chat-turn-bot">
              <BrandTile brand={brand} logoUrl={logoUrl} size="message" />
              <p className="ds-chat-greet">{greeting}</p>
            </div>
            <p className="ds-chat-try">TRY ONE OF THESE</p>
            {SUGGESTIONS.map((text) => (
              <button
                key={text}
                type="button"
                className="ds-chat-chip"
                onClick={() => {
                  send(text);
                }}
              >
                {text}
              </button>
            ))}
          </>
        )}

        {messages.map((turn) => {
          if (turn.role === 'user') {
            return (
              <div key={turn.id} className="ds-chat-turn-user">
                <div className="ds-chat-user">{turn.text}</div>
                {userAvatarUrl !== null ? (
                  // Decorative: the surrounding turn already names it as the user's message.
                  <img className="ds-chat-user-avatar ds-chat-user-avatar--image" src={userAvatarUrl} alt="" aria-hidden="true" />
                ) : userInitials === null ? (
                  <span className="ds-chat-user-avatar ds-chat-user-avatar--empty" aria-hidden="true">
                    <UserIcon size={11} />
                  </span>
                ) : (
                  <span className="ds-chat-user-avatar" aria-hidden="true">
                    {userInitials}
                  </span>
                )}
              </div>
            );
          }
          if (turn.role === 'assistant') {
            return <BotAnswer key={turn.id} turn={turn} brand={brand} logoUrl={logoUrl} />;
          }
          // A turn the agent could not answer reads as an ordinary assistant
          // message: the same wrapper, avatar and bubble as `BotAnswer`, with a
          // quiet retry in place of the chart and actions. It is the assistant
          // speaking, so it must carry the assistant's face — a bare bubble with
          // no avatar reads as a broken answer rather than a reply.
          // `status`, not `alert`: failing to answer is not an error condition
          // worth interrupting a screen reader for.
          return (
            // `data-chat-turn` is the hook that survives the visual unification:
            // now that a failed turn wears the same classes as a real answer,
            // nothing else could tell them apart, and a test that waited for
            // `.ds-chat-bot__text` would treat "I can't answer that" as an
            // answer. e2e/support/chat.ts keys off this attribute.
            <div key={turn.id} className="ds-chat-turn-bot" data-chat-turn="unanswered">
              <BrandTile brand={brand} logoUrl={logoUrl} size="message" />
              <div className="ds-chat-bot">
                <div className="ds-chat-bot__text" role="status">
                  {turn.text}
                </div>
                <div className="ds-chat-actions">
                  <button type="button" className="ds-chat-actions__link" onClick={retry}>
                    Try again
                  </button>
                </div>
              </div>
            </div>
          );
        })}

        {status === 'thinking' && (
          <div className="ds-chat-thinking" aria-label="Thinking" role="status">
            <span className="ds-chat-thinking__dot" style={{ animationDelay: '0s' }} />
            <span className="ds-chat-thinking__dot" style={{ animationDelay: '0.2s' }} />
            <span className="ds-chat-thinking__dot" style={{ animationDelay: '0.4s' }} />
          </div>
        )}
      </div>

      <form
        className="ds-chat-composer"
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        <input
          ref={inputRef}
          className="ds-chat-composer__input"
          type="text"
          placeholder="Ask about your documents…"
          aria-label="Ask about your documents"
          disabled={status === 'thinking'}
        />
        <button
          type="submit"
          className="ds-chat-send"
          aria-label="Send"
          title="Send"
          disabled={status === 'thinking'}
        >
          <SendGlyph />
        </button>
      </form>
    </section>
  );
}
